import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { VirtualVehicleVehicle } from '@/@types/VirtualVehicle';
import { useNavigate } from '@/providers/RouterShim';
import InputDateSingle from '../../../../components/HookFormInputs/InputDateSingle';
import useBranch from '../../../../data/branch/useBranch';
import useCreateVehicleOem, { newOemVehicle } from '../../../../data/vehicles/useCreateVehicleOem';
import type { DetailedVehicle } from '../../../../data/vehicles/useDetailedVehicle';
import usePurchaseInfoTypes from '../../../../data/vehicles/usePurchaseInfoTypes';
import useAuth from '../../../../hooks/useAuth';
import AddButton from '../../../../styled-components/components/AddButton';
import { Box } from '../../../../styled-components/components/Box';
import BulletSeparatedList from '../../../../styled-components/components/BulletSeparatedList';
import { Button } from '../../../../styled-components/components/Button';
import { Column, Columns } from '../../../../styled-components/components/Columns';
import { Form, FormGroup } from '../../../../styled-components/components/Form';
import { H5 } from '../../../../styled-components/components/Heading';
import InfoBox from '../../../../styled-components/components/InfoBox';
import InputSelect from '../../../../styled-components/components/InputSelect';
import InputText from '../../../../styled-components/components/InputText';
import { ShallowPanelBody } from '../../../../styled-components/components/Panel/ShallowPanel';
import SegmentedControl from '../../../../styled-components/components/SegmentedControl';
import { StackLarge, StackSmall } from '../../../../styled-components/components/Stack';
import { Text } from '../../../../styled-components/components/Text';
import { VStack } from '../../../../styled-components/components/VStack';
import { getMarketCode } from '../../../../utils/market';
import { purchaseInfoTypesLabel } from '../../Item/Shared/StatusInformationAside/PurchaseInfoType/PurchaseInfoHelpers';
import AddFromTemplate from './AddFromTemplate';
import { CreateVehicleFooter } from './Footer';
import type { CreateVehiclePanels } from './utils';
import {
  availableFrom,
  odometerReadingUnit,
  odometerReadingValue,
  purchaseType,
  virtualVehicleId,
} from './validationMethods';

const vehicleFormSchema = z.object({
  virtualVehicleId,
  odometerReadingValue,
  odometerReadingUnit,
  purchaseType,
  availableFrom,
});

type VehicleFormSchema = z.infer<typeof vehicleFormSchema>;

interface CreateOemVehicleProps {
  onClose: () => void;
  navigatePanel: (panel: CreateVehiclePanels) => void;
  showTemplateSelect: boolean;
  setShowTemplateSelect: (showTemplateSelect: boolean) => void;
}

const CreateOemVehicle = ({
  onClose,
  navigatePanel,
  showTemplateSelect,
  setShowTemplateSelect,
}: CreateOemVehicleProps) => {
  const { t } = useTranslation(['common', 'createVehiclePanel']);
  const { decodedToken } = useAuth();
  const [virtualVehicle, setVirtualVehicle] = useState<VirtualVehicleVehicle>();
  const [createVehicleOem, requestCreateVehicleOem, createVehicleResponseOem] =
    useCreateVehicleOem();
  const navigate = useNavigate();
  const [branch] = useBranch();
  const notAuthorized = !branch?.resellerFor.length;
  const marketCode = getMarketCode();
  const [purchaseInfoTypes] = usePurchaseInfoTypes();

  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
  } = useForm<VehicleFormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(vehicleFormSchema),
    defaultValues: {
      odometerReadingUnit: marketCode === 'SE' ? 'mil' : 'km',
    },
  });

  const onCreateVehicle = handleSubmit((data) => {
    const payload = {
      vehicle: newOemVehicle({
        detailedVehicle: {} as DetailedVehicle,
        odometerReadingValue: data.odometerReadingValue,
        odometerReadingUnit: data.odometerReadingUnit,
        purchaseInfoOfType: data.purchaseType ?? '',
        managedBy: decodedToken?.sub,
        oemId: virtualVehicle?.id,
        inventoryStatus: 'Incoming',
        availableFrom: data.availableFrom?.toISOString(),
      }),
    };

    createVehicleOem(payload);
  });

  useEffect(() => {
    if (requestCreateVehicleOem.isSuccess) {
      navigate(
        `/${branch?.id}/vehicles/administrate-vehicles/${createVehicleResponseOem?.vehicle.waykeId}?new=true`,
      );
      onClose();
    }
  }, [requestCreateVehicleOem.isSuccess]);

  if (showTemplateSelect) {
    return (
      <AddFromTemplate
        onSelect={(virtualVehicle) => {
          setValue('virtualVehicleId', virtualVehicle.id);
          setVirtualVehicle(virtualVehicle);
        }}
        onClose={() => setShowTemplateSelect(false)}
      />
    );
  }

  return (
    <>
      <ShallowPanelBody>
        <StackLarge>
          <H5 as="h3" $margin="small">
            {t('vehicleInfoHeading', { ns: 'createVehiclePanel' })}
          </H5>
          <Form>
            <FormGroup>
              <SegmentedControl
                label=""
                defaultActiveIndex={1}
                options={[
                  {
                    label: t('withRegVin', { ns: 'createVehiclePanel' }),
                    title: t('withRegVin', { ns: 'createVehiclePanel' }),
                    onClick: () => navigatePanel('REGISTERED'),
                  },
                  {
                    label: t('withoutRegVin', { ns: 'createVehiclePanel' }),
                    title: t('withoutRegVin', { ns: 'createVehiclePanel' }),
                    onClick: () => null,
                  },
                ]}
              />
            </FormGroup>
            <FormGroup>
              {!virtualVehicle ? (
                <StackSmall>
                  <AddButton
                    onClick={() => setShowTemplateSelect(true)}
                    title={t('selectTemplate', { ns: 'createVehiclePanel' })}
                    disabled={notAuthorized}
                    inputHeight
                  >
                    {t('selectTemplate', { ns: 'createVehiclePanel' })}
                  </AddButton>
                </StackSmall>
              ) : (
                <Box>
                  <Columns $spacing={2} $valign="center">
                    <Column $grow>
                      <VStack $spacing={0.5}>
                        <Text $size="small">
                          <Text as="span" $weight="bold">
                            {virtualVehicle.manufacturer}
                          </Text>{' '}
                          {virtualVehicle.salesName}
                        </Text>
                        <BulletSeparatedList
                          size="small"
                          tone="alt"
                          items={[
                            virtualVehicle.modelYear,
                            virtualVehicle.drivingWheel,
                            virtualVehicle.batteryCapacity
                              ? `${virtualVehicle.batteryCapacity}kWh`
                              : undefined,
                          ]}
                        />
                      </VStack>
                    </Column>
                    <Column $noShrink>
                      <Button
                        $tone="secondary"
                        $size="small"
                        onClick={() => setShowTemplateSelect(true)}
                        title={t('changeButton', { ns: 'createVehiclePanel' })}
                      >
                        {t('changeButton', { ns: 'createVehiclePanel' })}
                      </Button>
                    </Column>
                  </Columns>
                </Box>
              )}
              {notAuthorized && (
                <StackSmall>
                  <InfoBox>{t('unauthorizedRetailer', { ns: 'createVehiclePanel' })}</InfoBox>
                </StackSmall>
              )}
            </FormGroup>
            <FormGroup>
              <InputText
                label={t('odometerLabel', { ns: 'createVehiclePanel' })}
                type="number"
                register={register('odometerReadingValue', { valueAsNumber: true })}
                error={t(errors.odometerReadingValue?.message ?? '', { ns: 'createVehiclePanel' })}
                unitSelect={{
                  title: t('unit', { ns: 'glossary' }) || '',
                  id: 'wip-odometerReadingUnit',
                  register: register('odometerReadingUnit', { required: true }),
                  disabled: requestCreateVehicleOem.isLoading,
                  name: 'odometerReadingUnit',
                  options: [
                    { displayName: 'km', value: 'km' },
                    { displayName: 'mil', value: 'mil' },
                  ],
                }}
              />
            </FormGroup>
            <StackSmall>
              <FormGroup>
                <InputDateSingle
                  label={t('availableFromLabel', { ns: 'createVehiclePanel' })}
                  help={t('availableFromHelp', { ns: 'createVehiclePanel' })}
                  register={register('availableFrom')}
                  error={t(errors.availableFrom?.message ?? '', { ns: 'createVehiclePanel' })}
                  watch={watch}
                />
              </FormGroup>
            </StackSmall>
          </Form>
        </StackLarge>
        <StackLarge>
          <H5 as="h3" $margin="small">
            {t('moreInfoHeading', { ns: 'createVehiclePanel' })}
          </H5>
          <Form>
            <FormGroup>
              <InputSelect
                label={t('purchaseTypeLabel', { ns: 'createVehiclePanel' })}
                title={t('purchaseTypeTitle', { ns: 'createVehiclePanel' })}
                options={[
                  {
                    value: '',
                    displayName: t('purchaseTypeUnset', { ns: 'createVehiclePanel' }),
                    disabled: true,
                  },
                  ...(purchaseInfoTypes?.map((type) => ({
                    value: type.ofType,
                    displayName: purchaseInfoTypesLabel(type.ofType),
                  })) ?? []),
                ]}
                defaultValue=""
                help={t('purchaseTypeHelp', { ns: 'createVehiclePanel' })}
                register={register('purchaseType')}
                error={t(errors.purchaseType?.message ?? '')}
              />
            </FormGroup>
          </Form>
        </StackLarge>
      </ShallowPanelBody>
      <CreateVehicleFooter
        onSubmit={() => onCreateVehicle()}
        onClose={() => onClose()}
        disabled={!virtualVehicle}
        loading={requestCreateVehicleOem.isLoading}
      />
    </>
  );
};

export default CreateOemVehicle;
